<template>
  <v-layout class="auto-shrink">
    <PDFEditor
      v-if="selectedDocument"
      :key="documentPreviewData"
      :opened.sync="pdfEditorOpened"
      :pdf-src="documentPreviewData"
      :path="documentPath"
      :file-name="selectedDocument.name"
      :current-company-id="selectedEntity.id"
      :questionnaire-id="null"
      @on-saved="$emit('on-pdf-saved')"
    />
    <v-alert
      v-if="isEmpty(documents)"
      type="error"
      :value="true"
      outline
      block
      style="align-self: start"
    >
      No processed tax forms for {{ selectedYear }}.
    </v-alert>

    <v-layout column v-else>
      <v-layout align-center class="mb-2" style="flex: 0;">
        <v-tabs class="tabs py-0 my-0" v-model="activeTab" color="transparent">
          <v-tab
            v-for="formName in groupedFormNames"
            :key="formName"
            class="text-capitalize"
          >
            <v-layout no-wrap class="font-weight-bold">{{ formName }}</v-layout>
            <v-chip small>{{ forms(formName).length }}</v-chip>
          </v-tab>
          <!-- <v-tab
              v-for="scheduleName in otherSchedules"
              :key="scheduleName.name"
              class="text-capitalize"
            >
              <v-layout no-wrap class="font-weight-bold">{{
                scheduleName.name
              }}</v-layout>
            </v-tab> -->
        </v-tabs>
        <v-spacer />
        <v-switch
          v-model="hidePII"
          label="Hide PII"
          class="text-none ma-0 pa-0"
          hide-details
          style="max-width:100px"
        />
      </v-layout>
      <v-layout
        column
        fill-width
        v-if="groupedFormNames.length <= activeTab"
        class="text-xs-left"
      >
        <v-alert
          :value="true"
          outline
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="primary"
          class="text-xs-left mb-4"
        >
          This information is generated from the client's answers and documents
          from the {{ selectedYear }} questionnaire.
        </v-alert>
        <v-layout column align-center>
          <v-layout column align-start style="min-width: 500px">
            <v-flex
              v-for="field in activeSchedule.list"
              class="text-xs-left mb-2"
            >
              <span class="subheading"
                ><b>{{ field.label }}</b></span
              >
              <v-layout column class="text-xs-left" style="min-width: 500px">
                <v-layout
                  v-for="item in field.list"
                  align-center
                  class="mb-2"
                  :key="item.description"
                >
                  <v-flex xs5>
                    <v-text-field
                      solo
                      hide-details
                      placeholder="Description"
                      class="default-input"
                      :value="item.description"
                    />
                  </v-flex>
                  <v-flex xs7>
                    <v-layout align-center>
                      <v-text-field
                        solo
                        hide-details
                        prefix="$"
                        placeholder="Amount"
                        class="default-input"
                        :value="item.amount"
                      />
                      <v-tooltip bottom v-if="item.source" class="ml-2">
                        <v-icon slot="activator" class="primary--text"
                          >help</v-icon
                        >
                        <span>{{ item.source }}</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        class="ml-2"
                        @click="removeField(field, item)"
                        style="cursor: pointer"
                      >
                        <v-icon slot="activator" class="error--text"
                          >cancel</v-icon
                        >
                        <span>Delete</span>
                      </v-tooltip>
                    </v-layout>
                  </v-flex>
                </v-layout>
              </v-layout>
              <v-layout column align-center>
                <v-tooltip bottom>
                  <v-btn
                    slot="activator"
                    icon
                    class="default-input primary--text text-none"
                    @click="addField(field)"
                  >
                    <v-icon> add </v-icon>
                  </v-btn>
                  <span>Add Item</span>
                </v-tooltip>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-layout>

      <v-layout column fill-width v-else>
        <v-layout align-start>
          <preview-document
            v-if="previewLoaded && documentPreviewData"
            style="overflow: auto;"
            :preview-type="previewType"
            :preview-rotation="previewRotation"
            :hide-rotate-button="hideRotateButton"
            :document-preview-data="documentPreviewData"
          />

          <v-flex v-if="exportableForms.length">
            <v-layout column class="ma-2">
              <v-alert
                type="error"
                :value="true"
                outline
                class="text-xs-left"
                v-if="groupedFormNames[activeTab] === 'Other'"
              >
                These documents are not exportable
              </v-alert>

              <v-alert
                type="error"
                :value="true"
                outline
                class="text-xs-left"
                v-else-if="
                  !supportedTaxFormsForDataEntry.includes(
                    groupedFormNames[activeTab],
                  )
                "
              >
                {{ groupedFormNames[activeTab] }} are not currently exportable
              </v-alert>
              <v-layout align-center>
                <v-autocomplete
                  class="default-input"
                  hide-details
                  solo
                  :item-text="
                    item =>
                      item.detailed_name ? item.detailed_name : item.name
                  "
                  item-value="id"
                  :items="
                    matchedForm(selectedDocument)
                      ? forms(matchedForm(selectedDocument).name)
                      : forms('Other')
                  "
                  v-model="selectedDocumentId"
                >
                  <template slot="selection" slot-scope="data">
                    <v-layout no-wrap align-center>
                      {{
                        data.item.detailed_name
                          ? data.item.detailed_name
                          : data.item.name
                      }}
                      <v-chip small v-if="data.item.status === 'processing'">
                        <span>Processing</span>
                      </v-chip>
                    </v-layout>
                  </template>
                  <template slot="item" slot-scope="data">
                    {{
                      data.item.detailed_name
                        ? data.item.detailed_name
                        : data.item.name
                    }}
                    <v-chip small v-if="data.item.status === 'processing'">
                      <span>Processing</span>
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-layout>
              <v-layout align-center>
                <v-layout
                  align-center
                  v-if="selectedDocument.reviewer_id"
                  class="ma-2"
                  ><v-icon class="success--text mr-2">check_circle</v-icon
                  ><span
                    ><span
                      >Reviewed by {{ selectedDocument.reviewer_name }} on
                      {{
                        moment(selectedDocument.reviewed_at).format('l')
                      }}</span
                    >
                    <span
                      style="text-decoration: underline; cursor: pointer;"
                      class="primary--text font-weight-bold ml-2"
                      @click="toggleExtractedDocumentReviewed(selectedDocument)"
                      >Undo</span
                    >
                  </span></v-layout
                >
                <v-btn
                  class="default-input primary text-none pa-2"
                  @click="toggleExtractedDocumentReviewed(selectedDocument)"
                  v-if="
                    !selectedDocument.reviewer_id &&
                      !isEmpty(selectedDocument.extracted_document_fields)
                  "
                  block
                >
                  <span>Mark Reviewed</span>
                  <v-icon class="ml-2">check</v-icon>
                </v-btn>
                <v-layout shrink align-center no-wrap>
                  <v-layout
                    align-center
                    @click="previousDocument()"
                    class="grey--text text--darken-1 mr-3"
                  >
                    <v-icon
                      style="cursor: pointer"
                      :disabled="currentPage === 1"
                      large
                      >keyboard_arrow_left</v-icon
                    >
                  </v-layout>
                  <span
                    class="grey--text text--darken-3 subheading font-weight-bold"
                    shrink
                    >{{ currentPage }} / {{ pageCount }}</span
                  >
                  <v-layout
                    align-center
                    @click="nextDocument()"
                    class="grey--text text--darken-1 ml-1"
                  >
                    <v-icon
                      style="cursor: pointer"
                      :disabled="currentPage === pageCount"
                      large
                      >keyboard_arrow_right</v-icon
                    >
                  </v-layout>
                </v-layout>
              </v-layout>
            </v-layout>
            <v-layout
              class="ma-2"
              :class="
                biggerView
                  ? 'scrollable-bigger-container'
                  : 'scrollable-container'
              "
            >
              <GroupedTaxFormFields
                v-if="matchedForm(selectedDocument) && previewLoaded"
                :selected-document.sync="selectedDocument"
                :matched-form-for-selected-form="matchedForm(selectedDocument)"
                @update-status="(...args) => updateStatus(...args)"
                :documents="
                  selectedDocument
                    ? forms(matchedForm(selectedDocument).name)
                    : forms(groupedFormNames[0])
                "
                :show-all="!hidePII"
                @refresh="$emit('refresh')"
              />
            </v-layout>
          </v-flex>

          <v-flex v-else>
            <v-progress-circular
              indeterminate
              size="50"
              class="primary--text ma-5"
            />
          </v-flex>
        </v-layout>
      </v-layout>
    </v-layout>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import PreviewDocument from '@/components/PreviewDocument';
import API from '@/api';
import get from 'lodash/get';
import PDFEditor from '@/components/dialogs/PDFEditor';
import GroupedTaxFormFields from '@/components/forms/GroupedTaxFormFields.vue';
import UtilsMixins from '@/mixins/UtilsMixins';
import DataEntryMixin from '@/mixins/DataEntryMixin';
import { supportedExtensionsForPreview } from '@/constants/variables';
import { isEmpty } from 'lodash';
import moment from 'moment';

export default {
  name: 'ReviewTaxForms',

  components: {
    PreviewDocument,
    PDFEditor,
    GroupedTaxFormFields,
  },

  mixins: [UtilsMixins, DataEntryMixin],

  props: {
    selectedEntity: {
      type: Object,
    },

    selectedYear: {
      type: Number,
    },

    documents: {
      type: Array,
      default: [],
    },

    hideRotateButton: {
      type: Boolean,
    },

    selectedDocument: {
      type: Object,
    },

    biggerView: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      moment,
      currentPage: 1,
      zoom: true,
      hidePII: false,
      exportableForms: [],
      previewLoaded: false,
      supportedExtensionsForPreview,
      previewRotation: 0,
      documentExtension: '',
      newName: '',
      pdfEditorOpened: false,
      documentPreviewData: null,
      activeTab: 0,
      selectedDocumentId: null,
      activeSchedule: null,
      otherSchedules: [
        {
          name: 'Itemized Deductions',
          list: [
            {
              label: 'Medical & Dental Expenses',
              list: [],
            },
            {
              label: 'Taxes Paid',
              list: [
                {
                  description: 'Property Tax',
                  amount: '2834',
                  source: 'Client uploaded property tax statement',
                },
              ],
            },
            {
              label: 'Interest Paid',
              list: [
                {
                  description: 'Mortgage Interest',
                  amount: '24492',
                  source: 'Client uploaded 1098',
                },
              ],
            },
            {
              label: 'Gifts to Charity (Cash)',
              list: [
                {
                  description: 'Church of the Cross',
                  amount: '17000',
                  source: 'Client uploaded donation receipt',
                },
                {
                  description: 'Food for the Hungry',
                  amount: '504',
                  source: 'Client uploaded donation receipt',
                },
              ],
            },
            {
              label: 'Gifts to Charity (Non-Cash)',
              list: [],
            },
            {
              label: 'Casuaty & Theft Loss',
              list: [],
            },
            {
              label: 'Other Itemized Deductions',
              list: [],
            },
          ],
        },
        {
          name: 'Tax Payments',
          list: [
            {
              label: 'Federal Estimated Tax Payments',
              list: [
                {
                  description: 'Q1 payment',
                  amount: '2000',
                  source: "Client answered 'Paid $7000 for Q1'",
                },
              ],
            },
            {
              label: 'State Estimated Tax Payments',
              list: [],
            },
          ],
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
    }),

    ...mapState('companies', ['companies']),

    documentPath() {
      if (this.selectedDocument) {
        const path = this.selectedDocument.source
          .split('/')
          .slice(this.selectedDocument.source.startsWith('/') ? 3 : 2)
          .join('/');
        return '/' + path.substring(0, path.lastIndexOf('/'));
      }
    },
    groupedFormNames() {
      const list = this.preorderedForms.filter(value =>
        this.documents
          .map(a => a.name)
          .filter(this.onlyUnique)
          .includes(value),
      );
      if (this.forms('Other').length > 0) {
        list.push('Other');
      }
      return list;
    },
    pageCount() {
      if (this.matchedForm(this.selectedDocument)) {
        return this.forms(this.matchedForm(this.selectedDocument).name).length;
      } else {
        return this.forms('Other').length;
      }
    },
  },

  methods: {
    isEmpty,
    addField(field) {
      field.list.push({
        description: '',
        amount: '',
        source: 'Manually added by Firm User',
      });
    },
    removeField(field, item) {
      field.list.remove(item);
    },
    forms(formName) {
      if (formName === 'Other') {
        return this.documents
          .filter(
            d => !this.preorderedForms.includes(d.name) || d.name === 'Other',
          )
          .sort((a, b) => {
            return b.id - a.id;
          });
      } else {
        return this.documents
          .filter(d => d.name === formName)
          .sort((a, b) => {
            return b.id - a.id;
          });
      }
    },
    async previewDocument(document) {
      if (!document || !document.source) return;

      try {
        this.previewLoaded = false;
        this.previewType = this.endsWithAny(
          this.supportedExtensionsForPreview,
          document.source,
        );
        this.previewDocumentDialogOpened = true;

        const response = await API.extractedDocument.getPreviewAndExtractedDocumentFields(
          {
            extractedDocumentId: document.id,
            companyId: this.selectedEntity.id,
          },
        );
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was a problem loading the document.',
          });
        } else {
          if (this.previewType === 'heic') {
            this.documentPreviewData = await this.convertToJPEG(
              response.data.preview_link,
            );
          } else {
            this.documentPreviewData = response.data.preview_link;
          }
          this.selectedExtractedDocumentFields =
            response.data.extracted_document_fields;
          this.previewLoaded = true;
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem loading the document.',
        });
      } finally {
        this.selectedDocumentId = document.id;
      }
    },
    getDocumentName(link) {
      return link.split('/')[link.split('/').length - 1];
    },
    selectCorrectTab(document) {
      if (document) {
        this.activeTab = this.groupedFormNames.findIndex(
          a => a === document.name,
        );
        if (this.activeTab === -1) {
          this.activeTab = this.groupedFormNames.length - 1;
        } else {
          this.selectCorrectPage();
        }
      }
    },
    previousDocument() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.$emit(
          'update:selectedDocument',
          this.forms(this.matchedForm(this.selectedDocument).name)[
            this.currentPage - 1
          ],
        );
      }
    },
    nextDocument() {
      if (this.currentPage < this.pageCount) {
        this.currentPage++;
        this.$emit(
          'update:selectedDocument',
          this.forms(this.matchedForm(this.selectedDocument).name)[
            this.currentPage - 1
          ],
        );
      }
    },

    selectCorrectPage() {
      this.currentPage =
        this.forms(this.matchedForm(this.selectedDocument).name).findIndex(
          a => a.id === this.selectedDocument.id,
        ) + 1;
    },

    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
  },
  async created() {
    await this.getExportableForms();
    if (!this.selectedDocument) {
      this.$emit('update:selectedDocument', this.documents[0]);
      this.previewDocument(this.selectedDocument);
      this.selectCorrectTab(this.selectedDocument);
    }
    this.selectedDocumentId = this.selectedDocument.id;
  },
  mounted() {
    document.body.focus();
  },
  watch: {
    selectedDocument: {
      handler(document) {
        if (document) {
          this.previewDocument(document);
          this.selectCorrectTab(document);
        }
      },
    },
    documents: {
      handler(documents) {
        if (documents.length > 0) {
          this.$emit('update:selectedDocument', documents[0]);
          this.previewDocument(this.selectedDocument);
          this.selectCorrectTab(this.selectedDocument);
        }
      },
    },
    activeTab: {
      handler(tab) {
        const documentName = this.groupedFormNames[tab];
        if (documentName === 'Other') {
          this.$emit('update:selectedDocument', this.forms('Other')[0]);
        } else {
          this.$emit(
            'update:selectedDocument',
            this.documents.find(a => a.name === documentName),
          );
        }
        // if (this.groupedFormNames.length > tab) {
        //   const documentName = this.groupedFormNames[tab];
        //   this.selectedDocument = this.documents.find(
        //     a => a.name === documentName,
        //   );
        // } else {
        //   this.activeSchedule = this.otherSchedules[
        //     tab - this.groupedFormNames.length
        //   ];
        // }
      },
    },
    selectedYear: {
      handler() {
        this.activeTab = 0;
        const documentName = this.groupedFormNames[tab];
        this.$emit(
          'update:selectedDocument',
          this.documents.find(a => a.name === documentName),
        );
        this.selectedDocumentId = this.selectedDocument.id;
      },
    },
    selectedDocumentId: {
      handler(id) {
        this.$emit(
          'update:selectedDocument',
          this.documents.find(a => a.id === id),
        );
      },
    },
  },
};
</script>

<style scoped lang="postcss">
.scrollable-container {
  overflow: auto;
  max-height: 450px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}
.scrollable-bigger-container {
  overflow: auto;
  max-height: 550px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
}
</style>
